// MixpanelProvider.tsx
import React, { createContext, ReactNode, useContext, useEffect } from 'react';
import { useStores } from './Hooks';
import MixpanelService, { EventProperties, UserProperties } from './mixpanel';

// Define the context type with only core methods
interface MixpanelContextType {
    identify: (userId: string) => void;
    track: (eventName: string, properties?: EventProperties) => void;
    setUserProperties: (properties: UserProperties) => void;
    timeEvent: (eventName: string) => void;
}

// Create context with proper type
const MixpanelContext = createContext<MixpanelContextType | null>(null);

// Custom hook with type safety
export const useMixpanel = (): MixpanelContextType => {
    const context = useContext(MixpanelContext);
    if (!context) {
        throw new Error('useMixpanel must be used within MixpanelProvider');
    }
    return context;
};

// Provider props interface
interface MixpanelProviderProps {
    children: ReactNode;
}

export const MixpanelProvider: React.FC<MixpanelProviderProps> = ({ children }) => {
    const { user } = useStores().userStore;
    // Identify user when userId changes
    useEffect(() => {
        if (user.id) {
            MixpanelService.identify(user.id);
            if (user.email) {
                MixpanelService.setUserProperties({ $email: user.email });
            }
        }
    }, [user]);

    return <MixpanelContext.Provider value={MixpanelService}>{children}</MixpanelContext.Provider>;
};
